'use client';

import { Check } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { Coin } from '@/lib/api/transaction/models';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/lib/ui-kit/dropdown-menu';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';

export interface CurrencySelectProps {
  coins: Coin[];
  selectedId?: string;
  onSelect: (coin: Coin) => void;
}

interface CurrencyOptionProps {
  coin: Coin;
  isSelected: boolean;
  onSelect: (coin: Coin) => void;
}

function CurrencyOption({ coin, isSelected, onSelect }: CurrencyOptionProps) {
  const handleSelect = () => onSelect(coin);

  return (
    <DropdownMenuItem
      onSelect={handleSelect}
      className={cn(
        'flex cursor-pointer items-center justify-between gap-4 rounded-[8px] px-3 py-2 text-[13px] font-medium',
        'focus:bg-v2-neutral-surface focus:text-v2-text-primary dark:focus:bg-v2-surface-tertiary',
        isSelected ? 'text-v2-text-primary' : 'text-v2-text-secondary',
      )}
    >
      <span>{coin.shortName}</span>
      {isSelected ? <Check size={14} className="text-v2-text-primary" /> : null}
    </DropdownMenuItem>
  );
}

/** The hero's "USDC ▾" (Figma 14226:125642): ticker sits on the figure's baseline, arrow centred. */
export function CurrencySelect({ coins, selectedId, onSelect }: CurrencySelectProps) {
  const t = useTranslations('balancePopover');
  const selected = coins.find((c) => c.id === selectedId) ?? coins[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('selectCurrency')}
        className="inline-flex h-full cursor-pointer items-center gap-1 text-[15px] text-v2-text-secondary outline-none hover:text-v2-text-primary focus-visible:text-v2-text-primary"
      >
        <span className="self-end leading-[26px]">{selected?.shortName ?? 'USDC'}</span>
        <Icon name="ArrowSmallDown2" size={16} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="shadow-v2-card max-h-64 min-w-[160px] overflow-y-auto rounded-[10px] border border-v2-border bg-v2-surface-secondary p-1 dark:shadow-[0_4px_12px_rgba(0,0,0,0.4)]"
      >
        {coins.map((coin) => (
          <CurrencyOption
            key={coin.id}
            coin={coin}
            isSelected={coin.id === selected?.id}
            onSelect={onSelect}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
